import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import SubmissionQuickAccess from "@/components/SubmissionQuickAccess";
import useReveal from "@/hooks/useReveal";
import { SUBMISSION_HUB_CARDS } from "@/constants/formOptions";
import { track } from "@/lib/analytics";
import { scrollToFormTop } from "@/lib/scrollToForm";

const CARD_TONE_CLASS = {
  talent: "hub-card-tone-talent",
  "production-network": "hub-card-tone-crew",
  project: "hub-card-tone-project",
  partner: "hub-card-tone-partner",
  contact: "hub-card-tone-general",
};

function hashFromTo(to) {
  const i = String(to).indexOf("#");
  return i >= 0 ? String(to).slice(i + 1) : null;
}

function pathFromTo(to) {
  const i = String(to).indexOf("#");
  return i >= 0 ? String(to).slice(0, i) : String(to);
}

function HubCard({ card, index }) {
  const r = useReveal();
  const toneClass = CARD_TONE_CLASS[card.id] || "";
  const anchorId = hashFromTo(card.to);
  const pathname = pathFromTo(card.to);
  return (
    <article
      ref={r.ref}
      id={`hub-card-${card.id}`}
      className={`panel ${toneClass} ${r.className}`.trim()}
      data-testid={`submission-hub-card-${card.id}`}
      style={{
        transitionDelay: `${index * 60}ms`,
        display: "flex",
        flexDirection: "column",
        minHeight: 220,
        padding: 28,
        scrollMarginTop: "6rem",
      }}
    >
      {card.category && (
        <div className="card-meta" style={{ color: "var(--sokno-gold-warm)" }}>
          {card.category}
        </div>
      )}
      <h3
        className="font-display mt-3"
        style={{
          color: "var(--sokno-cream)",
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {card.title}
      </h3>
      <p
        className="mt-3"
        style={{
          color: "var(--sokno-cream-dim)",
          fontSize: 14,
          lineHeight: 1.65,
          flex: 1,
        }}
      >
        {card.audience}
      </p>
      <Link
        to={card.to}
        className="btn-gold mt-6"
        data-testid={`submission-hub-cta-${card.id}`}
        style={{ alignSelf: "flex-start", textDecoration: "none" }}
        onClick={() => {
          track("cta_click", {
            route: card.to,
            cta_label: card.cta,
            source_category: "submission_hub",
          });
          if (anchorId) {
            window.setTimeout(() => {
              scrollToFormTop(anchorId, { behavior: "auto", pathname });
            }, 0);
          }
        }}
      >
        {card.cta}
      </Link>
    </article>
  );
}

/**
 * Connect With SoKno — routes visitors to one dedicated submission path.
 * Does not embed full forms on the same page.
 */
export default function SubmissionHub({
  id = "connect-with-sokno",
  compact = false,
  heading = true,
}) {
  return (
    <section
      id={id}
      className="section section-tone section-tone-crew"
      data-testid="submission-hub-section"
      style={compact ? { paddingTop: 48, paddingBottom: 48 } : undefined}
    >
      <div className="container-x">
        {heading && (
          <SectionHeading
            eyebrow="Submissions"
            title="Connect"
            accent="With SoKno."
            testId="submission-hub-heading"
          >
            Choose the category that best fits your inquiry and submit the corresponding
            form. One submission is all you need.
          </SectionHeading>
        )}

        <SubmissionQuickAccess className={heading ? "mt-8" : ""} />

        <div
          className="mt-10"
          style={{
            display: "grid",
            gap: 18,
            gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
          }}
          data-testid="submission-hub-grid"
        >
          {SUBMISSION_HUB_CARDS.map((card, index) => (
            <HubCard key={card.id} card={card} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
